import 'react-phone-number-input/style.css'
import './style.css' // Force input styling

import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'
import es from 'react-phone-number-input/locale/es.json'

import { useFormErrors } from '@/hooks/useFormErrors'
import { useStep } from '@/hooks/useStep'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import { PhoneNumberComponent } from '@/types/components'
import { AVAILABLE_LANGUAGES } from '@/types/language'
import { STEP_TYPE } from '@/types/step'

export type PhoneNumberProps = Omit<PhoneNumberComponent, 'componentType'> & { error?: string; value?: string; tabIndex: number }

export const PhoneNumber = ({ id, textKey, required, placeholder, error, tabIndex, defaultCountry }: PhoneNumberProps) => {
  const { t } = useTranslation()
  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { formErrors, clearFormError, addFormError } = useFormErrors()

  const [value, setValue] = useState<string | null>(getUserDataById(id) as string | null)

  const handleValidation = (event: FocusEvent<HTMLInputElement, Element>) => {
    if (required && !event.target.value) {
      return addFormError(id, 'error.validation.required')
    }

    if (!isValidPhoneNumber(event.target.value)) {
      return addFormError(id, 'error.validation.phone')
    }
  }

  useEffect(() => {
    if (value === null) return

    if ([STEP_TYPE.USER_FORM, STEP_TYPE.CONSULTATION].includes(currentStep?.type as STEP_TYPE)) {
      return addData(id, value)
    }
  }, [value])

  const err = error || formErrors?.[id]

  return (
    <div className="flex w-full flex-col gap-1" data-testid="phone-number-container">
      <label htmlFor={id} className="font-bold">
        {t(textKey)} {required && '*'}
      </label>

      <PhoneInput
        id={id}
        data-testid={id}
        tabIndex={tabIndex}
        className={`rounded border p-2 ${err ? 'border-error' : 'border-gray'}`}
        defaultCountry={defaultCountry || 'US'}
        placeholder={placeholder}
        onChange={(value) => setValue(value ?? null)}
        onBlur={handleValidation}
        onFocus={() => clearFormError(id)}
        value={value ?? undefined}
        international
        autoComplete="none"
        labels={i18n.language === AVAILABLE_LANGUAGES.ES_MX ? es : en}
      />

      {err && (
        <span data-testid="phone-number-error-span" className="text-sm text-error">
          {t(err)}
        </span>
      )}
    </div>
  )
}
